import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";

import ActionButton from "../formInputs/buttons/ActionButton";
import isGradingProfileWindowOpen from "../../utils/ActionControls/isGradingProfileWindowOpen";
import isEprEditable from "../../utils/ActionControls/isEprEditable";
import LoadingSvg2 from "../loadingSvg/LoadingSvg2";
import SortHeader from "../shared/SortHeader";
import StudentImage from "../shared/StudentImage";
import useAutoSave from "../../hooks/useAutoSave";
import useSortConfig from "../../hooks/useSortConfig";
import useStudentImages from "../../hooks/useStudentImages";

import { getColorClass, RATING_OPTIONS, RATING_DISPLAY_LABELS } from "../../utils/teacher/InputRatingUtils";
import { compareGroupNameColumn, groupNameNormalizer } from "../../utils/GroupNameNormalizer";
import { makeSleKey, makeSleChildKey } from "../../utils/teacher/SleBatchUtils";

import "../../styles/RatingTable.scss";

/**
 * SLE batch table (homeroom + groups).
 *
 * - Visually matches RatingGrid (same wrapper/layout/classNames).
 * - Left side: fixed student columns.
 * - Right side:
 *   - SLE rating columns (parents + children) or
 *   - Single "Student SLE Comment" column (when showComments = true).
 * - Supports:
 *   - Clickable / selectable SLE column
 *   - Prev / Next column arrow navigation
 *   - "Selected School Level Expectation Description" box above table
 *
 * Props:
 * - `mode`: "HOMEROOM" | "GROUPS"
 * - `sleState`: normalized SLE state from homeroom or groups hook
 * - `students`: flattened list of students (with gradingProfileStudentId)
 */
const SleRatingTable = ({
    actionWindowsDto,
    mode,
    selectedEpr,
    teachersProfileStatus,
    sleState,
    students,
    token,
    schoolYearKey,
    basePath,
    userDetails
}) => {
    const {
        hasChanges,
        loading: sleSaving = false,
        onChangeSleComment,
        onChangeSleRating,
        saveSleRatings,
        schoolLevelExpectations,
        sleCommentsByStudent,
        valuesByStudent,
        groupRows,
        locationKey
    } = sleState;

    const navigate = useNavigate();

    const [activeColumnIndex, setActiveColumnIndex] = useState(0);
    const [isActiveWindow, setIsActiveWindow] = useState(false);
    const [showComments, setShowComments] = useState(false);

    const effectiveSchoolYearKey = selectedEpr?.schoolYearKey ?? schoolYearKey;
    const { images: studentImages, loading: imagesLoading } = useStudentImages(students, effectiveSchoolYearKey, token);
    const { sortConfig, requestSort } = useSortConfig(mode === "GROUPS" ? "groupName" : "lastName");

    const handleBulkSleRating = (col, rating) => {
        const uiKey = col.type === "parent" ? makeSleKey(col.sleId) : makeSleChildKey(col.childId);

        const isClear = rating == null || rating === "";

        students.forEach((student) => {
            const gpsId = student.gradingProfileStudentId;
            const studentValues = valuesByStudent[gpsId] || {};
            const current = studentValues[uiKey];

            if (isClear) {
                if (current === "EXCLUDED") return;
                if (current != null && current !== "") {
                    onChangeSleRating(gpsId, uiKey, null);
                }
            } else if (current == null || current === "") {
                onChangeSleRating(gpsId, uiKey, rating);
            }
        });
    };

    const handleOpenStudent = (student) => {
        const navMode = mode === "GROUPS" ? "GROUPS_SLE" : "HOMEROOM_SLE";
        const preselectedGroupIds = Array.isArray(groupRows) ? groupRows.map((g) => String(g.groupId)) : [];
        const sharedGroupId = student.groupId;
        const selectedSchoolLocationKey = locationKey ?? student.locationKey ?? student.profileLocationKey ?? null;

        const pathPrefix = basePath || "/home";
        navigate(
            {
                pathname: `${pathPrefix}/summary/input-ratings/student/${student.studentNumber}`,
                search: `?templateId=SLE`
            },
            {
                state: {
                    mode: navMode,
                    sharedGroupId,
                    preselectedGroupIds,
                    selectedSchoolLocationKey
                }
            }
        );
    };

    const sortedSles = (schoolLevelExpectations || []).slice().sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0));

    // Flatten parent + children into a single columns array
    const columns = useMemo(() => {
        const cols = [];
        for (const sle of sortedSles) {
            cols.push({
                type: "parent",
                sle,
                sleId: sle.key,
                child: null,
                childId: null,
                label: sle.expectation
            });

            for (const child of sle.sleChildList || []) {
                cols.push({
                    type: "child",
                    sle,
                    sleId: sle.key,
                    child,
                    childId: child.key,
                    label: child.expectation || child.description
                });
            }
        }
        return cols;
    }, [sortedSles]);

    useAutoSave(saveSleRatings, hasChanges && !sleSaving);

    // Ensure activeColumnIndex is always in range when SLE list changes
    useEffect(() => {
        if (activeColumnIndex >= columns.length) {
            setActiveColumnIndex(0);
        }
    }, [columns.length, activeColumnIndex]);

    const renderStudentImage = (student) => (
        <StudentImage
            src={studentImages[student.studentNumber]}
            firstName={student.firstName}
            lastName={student.lastName}
        />
    );

    const sortedStudents = useMemo(() => {
        if (!Array.isArray(students)) return [];
        const copy = [...students];

        if (mode === "GROUPS" && sortConfig.key === "groupName") {
            return copy.sort((a, b) => compareGroupNameColumn(a, b, sortConfig.direction));
        }

        const key = sortConfig.key;
        return copy.sort((a, b) => {
            const aVal = (a[key] || "").toString().toLowerCase();
            const bVal = (b[key] || "").toString().toLowerCase();
            if (sortConfig.direction === "ascending") {
                return aVal.localeCompare(bVal);
            }
            return bVal.localeCompare(aVal);
        });
    }, [students, sortConfig, mode]);

    const fixedRef = useRef(null);
    const scrollRef = useRef(null);
    const columnRefs = useRef([]);

    // Keep fixed + scrollable halves vertically in sync
    useEffect(() => {
        if (imagesLoading) return;

        const fixed = fixedRef.current;
        const scroll = scrollRef.current;
        if (!fixed || !scroll) return;

        const onFixedScroll = () => {
            scroll.scrollTop = fixed.scrollTop;
        };

        const onScroll = () => {
            fixed.scrollTop = scroll.scrollTop;
        };

        scroll.addEventListener("scroll", onScroll);
        fixed.addEventListener("scroll", onFixedScroll);

        return () => {
            scroll.removeEventListener("scroll", onScroll);
            fixed.removeEventListener("scroll", onFixedScroll);
        };
    }, [imagesLoading, students.length, columns.length, showComments]);

    // Scroll horizontally to keep active column in view
    useEffect(() => {
        const scrollContainer = scrollRef.current;
        const activeEl = columnRefs.current[activeColumnIndex];

        if (!scrollContainer || !activeEl) return;

        const scrollLeft = activeEl.offsetLeft - scrollContainer.offsetLeft;
        const scrollRightEdge = scrollLeft + activeEl.offsetWidth;

        if (scrollLeft < scrollContainer.scrollLeft) {
            scrollContainer.scrollTo({ left: scrollLeft - 20, behavior: "smooth" });
        } else if (scrollRightEdge > scrollContainer.scrollLeft + scrollContainer.clientWidth) {
            scrollContainer.scrollTo({
                left: scrollRightEdge - scrollContainer.clientWidth + 20,
                behavior: "smooth"
            });
        }
    }, [activeColumnIndex, columns.length]);

    useEffect(() => {
        if (actionWindowsDto) {
            let profileTimePeriod = null;
            if (mode === "GROUPS" && groupRows?.length > 0) {
                profileTimePeriod = groupRows[0].timePeriod;
            } else if (mode === "HOMEROOM" && selectedEpr) {
                profileTimePeriod = selectedEpr.timePeriod;
            }

            if (profileTimePeriod) {
                const isWindowOpen = isGradingProfileWindowOpen(actionWindowsDto, profileTimePeriod);
                setIsActiveWindow(isWindowOpen);
            }
        }
    }, [actionWindowsDto, groupRows, mode, selectedEpr]);

    // Auto-save before switching between ratings and comments views
    const handleToggleComments = () => {
        if (hasChanges && !sleSaving) {
            saveSleRatings();
        }
        setShowComments((prev) => !prev);
    };

    const handlePrevColumn = () => {
        setActiveColumnIndex((prev) => Math.max(0, prev - 1));
    };

    const handleNextColumn = () => {
        setActiveColumnIndex((prev) => Math.min(columns.length - 1, prev + 1));
    };

    if (imagesLoading) {
        return <LoadingSvg2 />;
    }

    if (sortedSles.length < 1) {
        return (
            <section className="no-sles-available">
                There are no School Level Expectations available for rating.
            </section>
        );
    }
    if (!students.length || !columns.length) return null;

    const activeColumn = columns[activeColumnIndex];
    const anyReleasedEprs =
        selectedEpr?.status === "READY_FOR_RELEASE" ||
        teachersProfileStatus === "READY_FOR_RELEASE" ||
        students?.some((student) => student.groupGradingProfileStatus === "READY_FOR_RELEASE");

    // For teachers who have no own EPR (selectedEpr is null and teachersProfileStatus is
    // undefined), fall back to the grading profile status from the shared students so that
    // isEprEditable can still return true when the window is open. We only want NOT_RELEASED
    // here — if all students are released there's nothing to edit and the fallback stays
    // undefined, which correctly disables Save.
    const sleProfileStatus =
        teachersProfileStatus ||
        selectedEpr?.status ||
        students?.find((s) => s.groupGradingProfileStatus === "NOT_RELEASED")?.groupGradingProfileStatus;

    return (
        <section>
            <div
                className={classNames("rating-column-description-wrapper", {
                    hide: showComments
                })}
            >
                <h5 className="rating-column-desc-title">Selected School Level Expectation Description:</h5>
                <div className="rating-column-description">
                    {activeColumn && (
                        <>
                            <div className="hierarchy-text-wrapper">
                                {activeColumn.type === "child" ? (
                                    <span className="hierarchy-text">Expectation:</span>
                                ) : (
                                    <strong>Selected Expectation:</strong>
                                )}
                                <span style={{ whiteSpace: "pre-line" }}>{activeColumn.sle.expectation}</span>
                            </div>
                            {activeColumn.type === "child" && <hr />}
                            {activeColumn.type === "child" && (
                                <div className="hierarchy-text-wrapper">
                                    <strong>Selected Sub-Expectation: </strong>
                                    <span style={{ whiteSpace: "pre-line" }}>{activeColumn.label}</span>
                                </div>
                            )}

                            {activeColumn.sle.description && (
                                <>
                                    <hr />
                                    <div>
                                        <strong>Details:</strong>
                                        <p style={{ whiteSpace: "pre-line" }}>{activeColumn.sle.description}</p>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Top button row: Show/Hide and Prev/Next column nav */}
            <div className="rating-table-btn-group" style={{ marginBottom: "8px" }}>
                <ActionButton
                    label={showComments ? "Hide Student Comments" : "Show Student Comments"}
                    onClick={handleToggleComments}
                />
                <div className="column-nav">
                    <ActionButton
                        className="action-button-80"
                        disable={showComments || activeColumnIndex === 0}
                        label="← Prev"
                        onClick={handlePrevColumn}
                    />
                    <ActionButton
                        className="action-button-80"
                        disable={showComments || activeColumnIndex === columns.length - 1}
                        label="Next →"
                        onClick={handleNextColumn}
                    />
                </div>
            </div>

            <div className="ratings-table-wrapper">
                <div className="table-split-wrapper">
                    {/* Fixed student columns */}
                    <div className="fixed-columns" ref={fixedRef}>
                        <table className="rating-table">
                            <thead>
                                <tr>
                                    {mode === "GROUPS" && (
                                        <th>
                                            <SortHeader
                                                label="Group"
                                                columnKey="groupName"
                                                sortConfig={sortConfig}
                                                onSort={requestSort}
                                            />
                                        </th>
                                    )}
                                    <th></th>
                                    <th>
                                        <SortHeader
                                            label="Last Name"
                                            columnKey="lastName"
                                            sortConfig={sortConfig}
                                            onSort={requestSort}
                                        />
                                    </th>
                                    <th>
                                        <SortHeader
                                            label="First Name"
                                            columnKey="firstName"
                                            sortConfig={sortConfig}
                                            onSort={requestSort}
                                        />
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {sortedStudents.map((student) => (
                                    <tr key={student.gradingProfileStudentId ?? student.key}>
                                        {mode === "GROUPS" && (
                                            <td>
                                                <div className="group-cell">
                                                    <b>{groupNameNormalizer(student.groupName)}</b>
                                                    {student.isShared && (
                                                        <div className="group-text">{student.groupHomeroomTeacher}</div>
                                                    )}
                                                    {student.isShared && student.groupPermission === "VIEW" && (
                                                        <div className="group-text view-text">View Only</div>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                        <td>{renderStudentImage(student)}</td>
                                        <td>
                                            <button
                                                type="button"
                                                className="as-link"
                                                onClick={() => handleOpenStudent(student)}
                                            >
                                                {student.lastName}
                                            </button>
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="as-link"
                                                onClick={() => handleOpenStudent(student)}
                                            >
                                                {student.firstName}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Scrollable side */}
                    <div className="scrollable-columns" ref={scrollRef}>
                        <table className="rating-table" style={showComments ? { width: "100%" } : {}}>
                            <thead>
                                {showComments ? (
                                    <tr>
                                        <th style={{ fontSize: "1rem" }}>Comments</th>
                                    </tr>
                                ) : (
                                    <tr>
                                        {columns.map((col, index) => (
                                            <th
                                                key={
                                                    col.type === "parent"
                                                        ? `sle-${col.sleId}`
                                                        : `sle-child-${col.childId}`
                                                }
                                                className={
                                                    index === activeColumnIndex ? "active-rating-col" : undefined
                                                }
                                                ref={(el) => {
                                                    columnRefs.current[index] = el;
                                                }}
                                            >
                                                <button
                                                    type="button"
                                                    className="clickable-header"
                                                    onClick={() => setActiveColumnIndex(index)}
                                                >
                                                    <span className={col.type === "child" ? "small" : undefined}>
                                                        {col.label.slice(0, 25)}
                                                    </span>
                                                </button>
                                                <select
                                                    aria-label={`Bulk rating for ${col.label}`}
                                                    disabled={
                                                        sleSaving ||
                                                        imagesLoading ||
                                                        students.some((stud) => stud.groupPermission === "VIEW") ||
                                                        anyReleasedEprs ||
                                                        !isEprEditable({
                                                            isActiveWindow,
                                                            status: sleProfileStatus,
                                                            userDetails
                                                        })
                                                    }
                                                    value=""
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        handleBulkSleRating(
                                                            col,
                                                            val === "__CLEAR__" ? null : val || null
                                                        );
                                                    }}
                                                >
                                                    <option value="">Rate for All</option>
                                                    {RATING_OPTIONS.map((opt) => (
                                                        <option key={opt} value={opt}>
                                                            {RATING_DISPLAY_LABELS[opt] || opt}
                                                        </option>
                                                    ))}
                                                    <option value="__CLEAR__">Clear Ratings</option>
                                                </select>
                                            </th>
                                        ))}
                                    </tr>
                                )}
                            </thead>
                            <tbody>
                                {sortedStudents.map((student) => {
                                    const gpsId = student.gradingProfileStudentId;
                                    const studentValues = valuesByStudent[gpsId] || {};
                                    const sleCommentRow = sleCommentsByStudent[gpsId] || {
                                        comment: ""
                                    };
                                    const perm = student.groupPermission;
                                    const gradingProfileStatus = teachersProfileStatus || selectedEpr?.status;
                                    const studentProfileStatus = student.isShared
                                        ? student.groupGradingProfileStatus
                                        : gradingProfileStatus;
                                    const studentIsActiveAndIsUnreleased = isEprEditable({
                                        isActiveWindow,
                                        status: studentProfileStatus,
                                        userDetails
                                    });
                                    const disableEditStudent =
                                        sleSaving || perm === "VIEW" || !studentIsActiveAndIsUnreleased;
                                    return (
                                        <tr key={student.gradingProfileStudentId ?? student.key}>
                                            {showComments ? (
                                                <td>
                                                    <textarea
                                                        className="student-criteria-comment-input"
                                                        disabled={disableEditStudent}
                                                        style={{ width: "100%" }}
                                                        rows={2}
                                                        value={sleCommentRow.comment || ""}
                                                        onChange={(e) => onChangeSleComment(gpsId, e.target.value)}
                                                    />
                                                </td>
                                            ) : (
                                                <>
                                                    {columns.map((col, index) => {
                                                        const uiKey =
                                                            col.type === "parent"
                                                                ? makeSleKey(col.sleId)
                                                                : makeSleChildKey(col.childId);
                                                        const value = studentValues[uiKey] || "";

                                                        return (
                                                            <td
                                                                key={
                                                                    col.type === "parent"
                                                                        ? `cell-${gpsId}-sle-${col.sleId}`
                                                                        : `cell-${gpsId}-sle-child-${col.childId}`
                                                                }
                                                                className={
                                                                    index === activeColumnIndex
                                                                        ? "active-rating-col"
                                                                        : undefined
                                                                }
                                                            >
                                                                <div className="rating-cell">
                                                                    {disableEditStudent ? (
                                                                        <div
                                                                            className={classNames(
                                                                                "view-only-rating",
                                                                                getColorClass(value)
                                                                            )}
                                                                        >
                                                                            {" "}
                                                                            {RATING_DISPLAY_LABELS[value] ||
                                                                                value ||
                                                                                "Not Yet Rated"}
                                                                        </div>
                                                                    ) : (
                                                                        <select
                                                                            aria-label={`Rating for ${student.firstName} ${student.lastName} on ${col.label}`}
                                                                            className={classNames(getColorClass(value))}
                                                                            disabled={
                                                                                sleSaving ||
                                                                                imagesLoading ||
                                                                                perm === "VIEW" ||
                                                                                !studentIsActiveAndIsUnreleased
                                                                            }
                                                                            value={value}
                                                                            onChange={(e) =>
                                                                                onChangeSleRating(
                                                                                    gpsId,
                                                                                    uiKey,
                                                                                    e.target.value || null
                                                                                )
                                                                            }
                                                                        >
                                                                            <option value="">Select...</option>
                                                                            {RATING_OPTIONS.map((opt) => (
                                                                                <option key={opt} value={opt}>
                                                                                    {RATING_DISPLAY_LABELS[opt] || opt}
                                                                                </option>
                                                                            ))}
                                                                            <option value="">Clear Rating</option>
                                                                        </select>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        );
                                                    })}
                                                </>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <div className="save-btn-ratings">
                <ActionButton
                    label="Save"
                    disable={
                        sleSaving ||
                        !hasChanges ||
                        !isEprEditable({ isActiveWindow, status: sleProfileStatus, userDetails })
                    }
                    onClick={saveSleRatings}
                />
            </div>
        </section>
    );
};

SleRatingTable.propTypes = {
    actionWindowsDto: PropTypes.instanceOf(Array),
    mode: PropTypes.oneOf(["HOMEROOM", "GROUPS"]).isRequired,
    sleState: PropTypes.shape({
        valuesByStudent: PropTypes.object.isRequired,
        sleCommentsByStudent: PropTypes.object.isRequired,
        onChangeSleRating: PropTypes.func.isRequired,
        onChangeSleComment: PropTypes.func.isRequired,
        hasChanges: PropTypes.bool,
        loading: PropTypes.bool,
        locationKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        saveSleRatings: PropTypes.func,
        schoolLevelExpectations: PropTypes.array,
        groupRows: PropTypes.instanceOf(Array)
    }).isRequired,
    students: PropTypes.arrayOf(
        PropTypes.shape({
            gradingProfileStudentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            studentNumber: PropTypes.string.isRequired,
            firstName: PropTypes.string.isRequired,
            lastName: PropTypes.string.isRequired
        })
    ).isRequired,
    selectedEpr: PropTypes.shape({
        key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        schoolYearKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        status: PropTypes.string,
        timePeriod: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    }),
    teachersProfileStatus: PropTypes.string,
    token: PropTypes.string.isRequired,
    schoolYearKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    basePath: PropTypes.string,
    userDetails: PropTypes.object
};

SleRatingTable.defaultProps = {
    actionWindowsDto: [],
    selectedEpr: null,
    schoolYearKey: null,
    basePath: ""
};

export default SleRatingTable;
